import { useState } from 'react';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import { X, PhoneCall, CheckCircle2, Loader2 } from 'lucide-react';
import { useLang } from '../i18n';

export default function LlamamosModal({ onClose }) {
    const { t } = useLang();
    const [form, setForm] = useState({ nombre: '', telefono: '' });
    const [status, setStatus] = useState('idle');

    const submit = async (e) => {
        e.preventDefault();
        if (status === 'sending') return;
        setStatus('sending');
        try {
            const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/callbacks`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(form),
            });
            if (!res.ok) throw new Error('bad response');
            setStatus('success');
            toast.success(t('llamamos_ok_t'));
        } catch (err) {
            setStatus('error');
            toast.error(t('llamamos_error'));
        }
    };

    const firstName = form.nombre ? `, ${form.nombre.split(' ')[0]}` : '';

    return (
        <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="fixed inset-0 z-[70] bg-ink/50 backdrop-blur-sm flex items-center justify-center p-4"
            data-testid="llamamos-modal-overlay"
        >
            <motion.div
                initial={{ opacity: 0, scale: 0.94, y: 16 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.96 }}
                transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
                onClick={(e) => e.stopPropagation()}
                className="w-full max-w-md rounded-[1.6rem] bg-white border border-ink/10 p-8 relative shadow-[0_40px_80px_-30px_rgba(27,42,74,0.5)]"
                data-testid="llamamos-modal"
            >
                <button
                    onClick={onClose}
                    aria-label="Cerrar"
                    className="absolute top-4 right-4 w-9 h-9 rounded-full border border-ink/15 flex items-center justify-center text-ink/60 hover:text-crimson hover:border-crimson transition-colors"
                    data-testid="llamamos-close"
                >
                    <X size={16} />
                </button>

                {status === 'success' ? (
                    <div className="text-center py-6" data-testid="llamamos-success">
                        <span className="w-16 h-16 rounded-full bg-pastelblue flex items-center justify-center mx-auto">
                            <CheckCircle2 size={30} className="text-crimson" />
                        </span>
                        <h3 className="font-display text-2xl font-semibold text-ink mt-6">{t('llamamos_ok_t')}</h3>
                        <p className="mt-3 text-ink/70 leading-relaxed">
                            {t('llamamos_ok_text').replace('{name}', firstName)}
                        </p>
                        <button
                            onClick={onClose}
                            className="mt-7 rounded-full border border-ink/20 px-6 py-3 text-[12px] font-bold uppercase tracking-[0.14em] text-ink hover:border-crimson hover:text-crimson transition-colors"
                            data-testid="llamamos-ok-close"
                        >
                            {t('llamamos_cerrar') || 'Cerrar'}
                        </button>
                    </div>
                ) : (
                    <>
                        <div className="w-12 h-12 rounded-2xl bg-crimson text-pastelcream flex items-center justify-center">
                            <PhoneCall size={22} strokeWidth={1.8} />
                        </div>
                        <h3 className="font-display text-2xl font-semibold text-ink mt-5">{t('llamamos_title')}</h3>
                        <p className="mt-2 text-[15px] text-ink/65 leading-relaxed">{t('llamamos_sub')}</p>

                        <form onSubmit={submit} className="mt-6 space-y-4" data-testid="llamamos-form">
                            <div>
                                <label className="text-[11px] font-bold uppercase tracking-[0.16em] text-ink/60">{t('llamamos_nombre')} *</label>
                                <input required minLength={2} value={form.nombre} onChange={(e) => setForm({ ...form, nombre: e.target.value })} placeholder={t('llamamos_ph_nombre')} className="w-full mt-2 rounded-xl border border-ink/15 px-4 py-3 text-[15px] focus:border-crimson focus:outline-none" data-testid="llamamos-input-nombre" />
                            </div>
                            <div>
                                <label className="text-[11px] font-bold uppercase tracking-[0.16em] text-ink/60">{t('llamamos_telefono')} *</label>
                                <input required minLength={6} value={form.telefono} onChange={(e) => setForm({ ...form, telefono: e.target.value })} placeholder={t('llamamos_ph_telefono')} className="w-full mt-2 rounded-xl border border-ink/15 px-4 py-3 text-[15px] focus:border-crimson focus:outline-none" data-testid="llamamos-input-telefono" />
                            </div>
                            <button
                                type="submit"
                                disabled={status === 'sending'}
                                className="w-full inline-flex items-center justify-center gap-2.5 rounded-full bg-crimson px-8 py-4 text-[13px] font-bold uppercase tracking-[0.14em] text-pastelcream transition-all hover:bg-crimsondark hover:scale-[1.02] active:scale-95 disabled:opacity-60"
                                data-testid="llamamos-submit"
                            >
                                {status === 'sending' ? <Loader2 size={16} className="animate-spin" /> : <PhoneCall size={16} />}
                                {status === 'sending' ? t('llamamos_enviando') : t('llamamos_send')}
                            </button>
                        </form>
                    </>
                )}
            </motion.div>
        </motion.div>
    );
}
